import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { AvatarCropper } from "@/components/common/AvatarCropper";
import { FileDropZone, type FileDropZoneHandle } from "@/components/common/FileDropZone";
import { ImageUpload } from "@/components/common/ImageUpload";
import { usePendingScope } from "@/lib/pending-edits";
import { MAX_BRAND_LOGO_BYTES, MB, RASTER_IMAGE_ACCEPT } from "@/lib/upload-limits";
import { userErrorMessage } from "@/lib/user-error";
import { LocalizedError } from "@/lib/localized-error";
import {
  DEFAULT_ACCENT,
  DEFAULT_BG,
  DEFAULT_SURFACE,
  useThemePrefs,
} from "@/stores/theme-prefs";
import {
  SystemSettingsDocument,
  UpdateSystemSettingsDocument,
  type SystemSettingsDataInput,
} from "@/graphql/generated/operations";
import { brandLogoRefusal, useBrandLogoEditing } from "./use-brand-logo-editing";
import { WelcomeMessageLinkPicker } from "./WelcomeMessageLinkPicker";

// Branding + appearance (genesis systemSettings): app name, logo and the colour
// palette (background, surface, accent), persisted platform-wide in the
// systemSettings JSONB. The palette below applies live via theme-prefs; edits
// surface in the global SaveBar (smycles-toast) and are persisted together on
// Speichern (and hydrated on load via useHydrateThemeFromSystemSettings).
// The logo (spark#314) rides the same save as one more field of the patch; the
// sidebar draws it in place of the app name. The background image stays out:
// nothing in spark renders it.

function eq(a: string, b: string): boolean {
  return a.toUpperCase() === b.toUpperCase();
}

export function BrandingPanel() {
  const intl = useIntl();
  const { data, loading, refetch } = useQuery(SystemSettingsDocument);
  const [updateSettings] = useMutation(UpdateSystemSettingsDocument);

  const [appName, setAppName] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const welcomeMessageRef = useRef<HTMLTextAreaElement>(null);
  const initialized = useRef(false);
  const settings = data?.systemSettings;
  const logo = useBrandLogoEditing(settings?.logo);
  const logoZoneRef = useRef<FileDropZoneHandle>(null);
  useEffect(() => {
    if (initialized.current || !settings) return;
    initialized.current = true;
    setAppName(settings.appName ?? "");
    setWelcomeMessage(settings.welcomeMessage ?? "");
  }, [settings]);

  function insertIntoWelcomeMessage(markdown: string) {
    const textarea = welcomeMessageRef.current;
    if (!textarea) {
      setWelcomeMessage((v) => `${v}${markdown}`);
      return;
    }
    const { selectionStart, selectionEnd } = textarea;
    setWelcomeMessage((v) => v.slice(0, selectionStart) + markdown + v.slice(selectionEnd));
    requestAnimationFrame(() => {
      const cursor = selectionStart + markdown.length;
      textarea.focus();
      textarea.setSelectionRange(cursor, cursor);
    });
  }

  // Live palette (theme-prefs); persisted together with the app name.
  const bgColor = useThemePrefs((s) => s.bgColor);
  const surfaceColor = useThemePrefs((s) => s.surfaceColor);
  const accentColor = useThemePrefs((s) => s.accentColor);

  // Persisted baseline (systemSettings); null colours fall back to defaults so
  // a fresh install is not reported as dirty against the local defaults.
  const baseName = settings?.appName ?? "";
  const baseWelcomeMessage = settings?.welcomeMessage ?? "";
  const baseBg = settings?.backgroundColor ?? DEFAULT_BG;
  const baseSurface = settings?.surfaceColor ?? DEFAULT_SURFACE;
  const baseAccent = settings?.accentColor ?? DEFAULT_ACCENT;

  // Only the palette can differ from the baseline without anyone editing it: the colours live in
  // the app-wide theme store (hydrated from systemSettings, persisted in localStorage by zustand),
  // so they already hold a value before this panel has a baseline to judge them against. appName
  // and welcomeMessage are local draft state and cannot — which is why only one half is gated.
  const hasBaseline = data !== undefined;

  const textDirty = appName !== baseName || welcomeMessage !== baseWelcomeMessage;

  // Two guards, because a store/baseline mismatch has two innocent causes. Without a baseline the
  // base* above are just the hardcoded defaults, so any configured palette reads as an edit. And a
  // baseline of "no settings row" (systemSettings is nullable) makes the defaults genuinely the
  // baseline, where a palette left in localStorage by an earlier session still reads as one —
  // intent cannot be inferred from a persisted store, only from a change made while this panel was
  // on screen. Hydration writes the server's own values, so the baseline check below absorbs it.
  // useState, not useRef: this is read during render, and a ref read there is neither reactive nor
  // allowed by react-hooks/refs. The lazy initializer freezes the palette as it stood at mount.
  const [mountPalette] = useState(() => ({
    bg: bgColor,
    surface: surfaceColor,
    accent: accentColor,
  }));
  const paletteMoved =
    !eq(bgColor, mountPalette.bg) ||
    !eq(surfaceColor, mountPalette.surface) ||
    !eq(accentColor, mountPalette.accent);
  const paletteDirty =
    hasBaseline &&
    paletteMoved &&
    (!eq(bgColor, baseBg) || !eq(surfaceColor, baseSurface) || !eq(accentColor, baseAccent));

  // Not gated: SaveBar renders nothing while no scope is dirty, so gating a typed name would make
  // it unsaveable on a server whose settings query is failing.
  const dirty = textDirty || paletteDirty || logo.dirty;

  usePendingScope("branding", {
    dirty,
    save: async () => {
      // The payload carries what actually moved, nothing else. genesis merges the patch into the
      // stored row (SystemSettingsController: settings.putAll), so an omitted field keeps its
      // stored value — which is what HrPromptPanel already relies on to patch one field. Sending
      // the whole blob would write back state this panel may never have had a baseline for: a
      // text-only save would promote a palette left in localStorage into the settings row, and
      // would blank a welcome message that never hydrated. The palette rides on paletteDirty, not
      // on paletteMoved — otherwise a typed name would carry an unbaselined palette along with it.
      const settingsPatch: SystemSettingsDataInput = {};
      if (appName !== baseName) settingsPatch.appName = appName.trim();
      if (welcomeMessage !== baseWelcomeMessage) {
        settingsPatch.welcomeMessage = welcomeMessage.trim();
      }
      if (paletteDirty) {
        settingsPatch.accentColor = accentColor;
        settingsPatch.backgroundColor = bgColor;
        settingsPatch.surfaceColor = surfaceColor;
      }
      // Same rule for the logo: mentioned only when picked (the file) or removed (null).
      const logoPatch = logo.patch();
      if (logoPatch !== undefined) settingsPatch.logo = logoPatch;
      try {
        await updateSettings({ variables: { input: { data: settingsPatch } } });
        setAppName((n) => n.trim());
        setWelcomeMessage((m) => m.trim());
        await refetch();
        // Only after the refetch: until then the stored logo is still the old one, and the pick
        // is what keeps the control from flashing it.
        logo.settle();
      } catch (e) {
        // A refusal of the logo is said twice: beside the picker, where the admin will look for
        // it, and in the SaveBar (which shows a LocalizedError's message), which is what rejected.
        const refusal = brandLogoRefusal(intl, e);
        if (refusal) {
          logo.refuse(refusal);
          throw new LocalizedError(refusal, { cause: e });
        }
        // Surface a friendly message in the SaveBar (which shows a LocalizedError's message).
        throw new LocalizedError(userErrorMessage(intl, e), { cause: e });
      }
    },
    discard: () => {
      setAppName(baseName);
      setWelcomeMessage(baseWelcomeMessage);
      logo.discard();
      // Same flag as dirty and the payload: a palette this panel never claimed as an edit is not
      // its to undo. Resetting it on a text-only discard is how the original bug wiped the real
      // palette.
      if (!paletteDirty) return;
      const store = useThemePrefs.getState();
      store.setBgColor(baseBg);
      store.setSurfaceColor(baseSurface);
      store.setAccentColor(baseAccent);
    },
  });

  return (
    <div className="box bg-surface p-6">
      {/* The same cropper a person's photo and a company logo go through, in its wide frame. */}
      <AvatarCropper
        frame="wide"
        file={logo.cropFile}
        onCancel={logo.cancelCrop}
        onApply={logo.applyCrop}
        onInvalid={logo.rejectCrop}
      />
      <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">
        {intl.formatMessage({ id: "adminSettings.branding.title", defaultMessage: "Branding" })}
      </div>
      <div className="mt-1 text-smycles-meta text-ink-muted">
        {intl.formatMessage({
          id: "adminSettings.branding.sub",
          defaultMessage: "App name, logo and colour palette. Applies platform-wide.",
        })}
      </div>

      <div className="mt-5 space-y-4">
        <label className="block">
          <span className="text-smycles-meta text-ink-muted">
            {intl.formatMessage({ id: "adminSettings.branding.appName", defaultMessage: "App name" })}
          </span>
          <input
            id="branding-appname"
            value={appName}
            onChange={(e) => setAppName(e.target.value)}
            disabled={loading}
            placeholder={intl.formatMessage({ id: "adminSettings.branding.appNamePlaceholder", defaultMessage: "smycles" })}
            className="bg-surface mt-1 w-full rounded-md px-3 py-2 text-smycles-body ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui disabled:opacity-60"
          />
          <span className="mt-1 block text-smycles-label text-ink-muted">
            {intl.formatMessage({
              id: "adminSettings.branding.appNameHint",
              defaultMessage: "Shown top-left. Empty falls back to “smycles”.",
            })}
          </span>
        </label>

        <div>
          <span className="text-smycles-meta text-ink-muted">
            {intl.formatMessage({ id: "adminSettings.branding.logo", defaultMessage: "Logo" })}
          </span>
          <div className="mt-2 flex items-start gap-4">
            {/* The same control as the person's photo and the company logo, in its wide shape: the
                image IS the control, click to upload or replace, the instruction lives in the hover
                overlay. Removal is the affordance below it and is staged into the SaveBar, not
                written on the spot (see use-brand-logo-editing). */}
            <FileDropZone
              ref={logoZoneRef}
              inputId="branding-logo-input"
              variant="overlay"
              accept={RASTER_IMAGE_ACCEPT}
              multiple={false}
              disabled={loading}
              onFiles={logo.choose}
              className="shrink-0 rounded-md"
            >
              <ImageUpload
                id="branding-logo"
                shape="wide"
                editable
                busy={loading}
                hoverLabel={intl.formatMessage({
                  id: "adminSettings.branding.logoChangeHover",
                  defaultMessage: "Change logo",
                })}
                ariaLabel={
                  logo.shown
                    ? intl.formatMessage({
                        id: "adminSettings.branding.logoChange",
                        defaultMessage: "Change the logo",
                      })
                    : intl.formatMessage({
                        id: "adminSettings.branding.logoChoose",
                        defaultMessage: "Upload a logo",
                      })
                }
                onActivate={() => logoZoneRef.current?.openPicker()}
                remove={
                  logo.shown
                    ? {
                        label: intl.formatMessage({
                          id: "adminSettings.branding.logoRemove",
                          defaultMessage: "Remove logo",
                        }),
                        onRemove: logo.remove,
                      }
                    : null
                }
                style={{ width: 176, height: 64 }}
              >
                {logo.shown ? (
                  // Decorative: the button's aria-label says what this is.
                  <img
                    id="branding-logo-image"
                    src={logo.shown}
                    alt=""
                    className="h-full w-full object-contain p-2"
                  />
                ) : (
                  <span className="text-smycles-label text-ink-muted">
                    {intl.formatMessage({
                      id: "adminSettings.branding.logoEmpty",
                      defaultMessage: "No logo",
                    })}
                  </span>
                )}
              </ImageUpload>
            </FileDropZone>
            <span className="text-smycles-label text-ink-muted">
              {intl.formatMessage(
                {
                  id: "adminSettings.branding.logoHint",
                  defaultMessage:
                    "Shown top-left in place of the app name. PNG, JPEG, GIF, BMP, TIFF or WebP up to {max, number} MB, cropped to the sidebar's 4:1; a transparent background works best.",
                },
                { max: MAX_BRAND_LOGO_BYTES / MB },
              )}
            </span>
          </div>
          {logo.message ? (
            <p id="branding-logo-message" className="smycles-field-meta mt-2 text-ink">
              {logo.message}
            </p>
          ) : null}
        </div>

        <div>
          <div className="flex items-center justify-between gap-2">
            <label htmlFor="welcomeMessage" className="text-smycles-meta text-ink-muted">
              {intl.formatMessage({
                id: "adminSettings.branding.welcomeMessage",
                defaultMessage: "Welcome message",
              })}
            </label>
            <WelcomeMessageLinkPicker onInsert={insertIntoWelcomeMessage} />
          </div>
          <textarea
            id="welcomeMessage"
            ref={welcomeMessageRef}
            value={welcomeMessage}
            onChange={(e) => setWelcomeMessage(e.target.value)}
            disabled={loading}
            rows={3}
            placeholder={intl.formatMessage({
              id: "adminSettings.branding.welcomeMessagePlaceholder",
              defaultMessage: "Shown as a pop-up on the next visit to the home page and stays there as a dismissable hint.",
            })}
            className="bg-surface mt-1 w-full rounded-md px-3 py-2 text-smycles-body ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui disabled:opacity-60"
          />
          <span className="mt-1 block text-smycles-label text-ink-muted">
            {intl.formatMessage({
              id: "adminSettings.branding.welcomeMessageHint",
              defaultMessage: "Supports links: [Text](/path). Everything else is shown as plain text.",
            })}
          </span>
        </div>
      </div>

      <p className="mt-4 text-smycles-label text-ink-muted">
        {intl.formatMessage({
          id: "adminSettings.branding.paletteNote",
          defaultMessage:
            "The palette below applies live; unsaved changes appear in the save bar and persist platform-wide on save.",
        })}
      </p>
    </div>
  );
}
